import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { prefetchAllData } from '@/hooks/useQueries';
import { clearUserCache } from '@/utils/auth.ts';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate input
      if (!formData.email) {
        setError('Please enter your email address');
        setLoading(false);
        return;
      }

      if (!formData.password) {
        setError('Please enter your password');
        setLoading(false);
        return;
      }

      const response = await authApi.login(
        formData.email,
        formData.password
      );

      if (response.token && response.user) {
        // Clear any previous user's cache
        clearUserCache(queryClient);
        
        // Prefetch all key data using React Query
        prefetchAllData(queryClient).catch(err => {
          console.error('Prefetch error (non-blocking):', err);
        });
        
        // Navigate to dashboard
        navigate('/dashboard');
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err: any) {
      // Handle errors
      const errorMessage = err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#50b3e1]/10 via-white to-[#0997a4]/10 flex items-center justify-center p-4">
      <div className="w-full max-w-sm mx-auto">
        {/* Logo & Title */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <img 
              src="/images/hic-logo.png" 
              alt="Heart Institute of the Caribbean"
              className="h-16 w-auto object-contain"
            />
          </div>
          {/* <h1 className="text-3xl font-bold text-gray-900 mb-2">HIC Stock Management</h1> */}
          {/* <p className="text-gray-600 text-sm">Sign in to access your dashboard</p> */}
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-md shadow-2xl border border-gray-200 w-full px-6 py-8">
          <h2 className="text-center text-xl font-semibold text-gray-900 tracking-tight mb-6">
            Sign In
          </h2>
          <form onSubmit={handleSubmit} className="space-y-5 w-full">
            {/* Success message from password change */}
            {location.state?.message && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center gap-2 text-green-700 text-sm">
                <i className="ri-checkbox-circle-line text-lg"></i>
                {location.state.message}
              </div>
            )}
            {error && (
              <div className="bg-[#ed1438]/10 border border-[#ed1438]/20 rounded-md p-3 flex items-center gap-2 text-[#ed1438] text-sm">
                <i className="ri-error-warning-line text-lg"></i>
                {error}
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Username
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Email"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Password"
                  className="w-full pl-9 pr-11 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onMouseDown={() => setShowPassword(true)}
                  onMouseUp={() => setShowPassword(false)}
                  onMouseLeave={() => setShowPassword(false)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  tabIndex={-1}
                >
                  <i className={showPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"}></i>
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-[#50b3e1] hover:text-[#0997a4] font-medium whitespace-nowrap cursor-pointer"
              >
                Forgot password?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#50b3e1] to-[#0997a4] text-white py-2 rounded-md text-sm font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Logging in...
                </span>
              ) : (
                'Login'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          © 2026 HIC Hospital. All rights reserved.
        </p>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-md shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Reset Password</h2>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail('');
                  setForgotPasswordError('');
                  setForgotPasswordSuccess(false);
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="px-6 py-6">
              {forgotPasswordSuccess ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl mt-0.5"></i>
                    <div>
                      <p className="text-sm font-medium text-green-900">Reset link sent!</p>
                      <p className="text-xs text-green-700 mt-1">
                        {import.meta.env.DEV 
                          ? 'Check the API response in console for the reset token (development mode only)'
                          : 'If that email exists, we have sent a password reset link to your email address.'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setForgotPasswordError('');
                      setForgotPasswordSuccess(false);
                    }}
                    className="w-full px-4 py-2 bg-gradient-to-r from-[#50b3e1] to-[#0997a4] text-white rounded-md text-sm font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setForgotPasswordError('');
                    setForgotPasswordLoading(true);

                    if (!forgotPasswordEmail.trim()) {
                      setForgotPasswordError('Please enter your email address');
                      setForgotPasswordLoading(false);
                      return;
                    }

                    try {
                      await authApi.forgotPassword(forgotPasswordEmail.trim());
                      setForgotPasswordSuccess(true);
                    } catch (err: any) {
                      setForgotPasswordError(err.message || 'Failed to send reset link');
                    } finally {
                      setForgotPasswordLoading(false);
                    }
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                      <input
                        type="email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        placeholder="Email"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  {forgotPasswordError && (
                    <div className="bg-[#ed1438]/10 border border-[#ed1438]/20 rounded-md p-3 flex items-center gap-2 text-[#ed1438] text-sm">
                      <i className="ri-error-warning-line text-lg"></i>
                      {forgotPasswordError}
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotPasswordEmail('');
                        setForgotPasswordError('');
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={forgotPasswordLoading}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-[#50b3e1] to-[#0997a4] text-white rounded-md text-sm font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {forgotPasswordLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Sending...
                        </span>
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
